import { useEffect, useState, useContext } from "react";
import { Box, Button } from "@mui/material";
import { motion } from "framer-motion";

import {
  MOTION_VAR_INITIAL,
  MOTION_VAR_ANIMATE,
  LIST_ANIMATE_VARIANTS,
} from "@constants/Animation.constants.js";
import { AuthContext } from "@contexts/AuthContext";
import { fetchAllModelImageData } from "@libs/apiRequest.js";
import useSearch from "@hooks/useSearch.js";
import useModelData from "@hooks/useModelData";
import Spinner from "@common-components/Spinner/Spinner";
import ErrorMessage from "@common-components/ErrorMessage/ErrorMessage";
import ModelImage from "@common-components/ModelImage/ModelImage";
import VersionPop from "@components/VersionPop/VersionPop";

const ModelGrid = ({
  onModelSelect,
  selectedTab,
  selectedProject,
  searchKey,
  excludedViewRef,
}) => {
  const { currentUserRole } = useContext(AuthContext);
  const [allImages, setAllImages] = useState({});

  // fetch all model images
  useEffect(() => {
    const getImages = async () => {
      const response = await fetchAllModelImageData();
      setAllImages(response); // store all model images
    };
    getImages();
  }, []);

  const { selectedVersions, activeList, error, isLoading } = useModelData(
    currentUserRole,
    selectedProject,
    selectedTab,
  );

  const filteredActiveList = useSearch(activeList, searchKey, [
    "code",
    "name",
    "group",
  ]);

  return (
    <Box
      ref={excludedViewRef}
      sx={{
        width: "100%",
        height: "80%",
        padding: "0px 19px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
          scrollbarColor: "grey transparent",
          backgroundClip: "border-box",
          paddingRight: "10px",
          marginTop: "40px",
          "@media (max-width: 1330px)": {
            marginTop: "0px",
          },
        }}
      >
        {isLoading.modelMenuData && isLoading.versionData ? (
          <Spinner />
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : activeList.length === 0 ? (
          <ErrorMessage>No {selectedTab} model available</ErrorMessage>
        ) : (
          <motion.div
            key={selectedTab}
            initial={MOTION_VAR_INITIAL}
            animate={MOTION_VAR_ANIMATE}
            variants={LIST_ANIMATE_VARIANTS.variants}
            transition={LIST_ANIMATE_VARIANTS.transition}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, 280px)",
                justifyContent: "center",
                gap: 2,
                width: "95%",
                margin: "0 auto",
              }}
            >
              {filteredActiveList
                .sort((a, b) =>
                  (a.code ?? "").localeCompare(b.code ?? "", undefined, {
                    sensitivity: "base",
                  }),
                )
                .map((model) => (
                  <div
                    key={model.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "white",
                      borderRadius: "6px",
                      gap: "15px",
                      padding: "15px",
                      marginTop: "10px",
                      marginBottom: "5px",
                      border: "1px solid rgba(128, 128, 128, 0.2)",
                      boxShadow:
                        "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ModelImage
                        model={model}
                        images={allImages[model.code] || []}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          width: "100%",
                          padding: "0 24px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{ fontWeight: "bold", color: "secondary.main" }}
                          >
                            {model.name}
                          </Box>
                          <Box
                            sx={{ color: "secondary.main", fontSize: "10px" }}
                          >
                            {model.code}
                          </Box>
                        </div>
                        <div>
                          <Box
                            sx={{
                              color: "#6B7280",
                              backgroundColor: "#E5E5E5",
                              padding: "2px 5px",
                              borderRadius: "6px",
                              fontSize: "10px",
                            }}
                          >
                            {model.group}
                          </Box>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          fontSize: "14px",
                          padding: "8px",
                          width:
                            currentUserRole === "ADMIN" ||
                            currentUserRole?.startsWith("ADMIN_") ||
                            currentUserRole === "MANAGE" ||
                            currentUserRole?.startsWith("MANAGE_")
                              ? "87%"
                              : "100%",
                          borderRadius: "6px",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "5%",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "primary.btn_background_color",
                            color: "white",
                          },
                        }}
                        onClick={() => {
                          const selectedVersion =
                            selectedTab === "SALES"
                              ? selectedVersions.sales_model_version[model.id]
                              : selectedTab === "COORDINATION"
                                ? selectedVersions.coordination_model_version[
                                    model.id
                                  ]
                                : selectedTab === "IFC"
                                  ? selectedVersions.design_model_version[
                                      model.id
                                    ]
                                  : "";
                          if (selectedVersion) {
                            onModelSelect({
                              ...model,
                              selectedVersion: selectedVersion,
                            });
                          } else {
                            alert("Please select a version first.");
                          }
                        }}
                      >
                        Select Options
                      </Button>

                      {(currentUserRole === "ADMIN" ||
                        currentUserRole?.startsWith("ADMIN_") ||
                        currentUserRole === "MANAGE" ||
                        currentUserRole?.startsWith("MANAGE_")) && (
                        <div style={{ width: "10%" }}>
                          <VersionPop
                            onModelSelect={onModelSelect}
                            model={model}
                            selectedTab={selectedTab}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </Box>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

export default ModelGrid;
